import { TrendingUp, TrendingDown, Calendar, Tag } from 'lucide-react'
import { Card } from '@/components/common'
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES, PAYMENT_METHODS } from '@/constants'
import type { Transaction } from '@/types'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'

interface TransactionCardProps {
  transaction: Transaction
  onClick?: () => void
}

export function TransactionCard({ transaction, onClick }: TransactionCardProps) {
  const isIncome = transaction.type === 'income'

  const categoryLabel = isIncome
    ? INCOME_CATEGORIES[transaction.category as keyof typeof INCOME_CATEGORIES]
    : EXPENSE_CATEGORIES[transaction.category as keyof typeof EXPENSE_CATEGORIES]

  const statusColors = {
    pending: 'text-warning bg-warning-light',
    completed: 'text-success bg-success-light',
    cancelled: 'text-error bg-error-light',
  }

  const statusLabels = {
    pending: 'รอดำเนินการ',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก',
  }

  return (
    <Card
      onClick={onClick}
      className="cursor-pointer hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left - Icon & Info */}
        <div className="flex items-start gap-4 flex-1">
          {/* Icon */}
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isIncome ? 'bg-success-light' : 'bg-error-light'
            }`}
          >
            {isIncome ? (
              <TrendingUp className="w-6 h-6 text-success" />
            ) : (
              <TrendingDown className="w-6 h-6 text-error" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-text-primary truncate">
                {categoryLabel}
              </h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  statusColors[transaction.status]
                }`}
              >
                {statusLabels[transaction.status]}
              </span>
            </div>

            {transaction.description && (
              <p className="text-sm text-text-secondary mb-2 line-clamp-1">
                {transaction.description}
              </p>
            )}

            <div className="flex items-center gap-4 text-sm text-text-tertiary">
              {/* Date */}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>
                  {format(new Date(transaction.date), 'dd MMM yyyy', { locale: th })}
                </span>
              </div>

              {/* Payment Method */}
              <div className="flex items-center gap-1">
                <Tag className="w-4 h-4" />
                <span>
                  {PAYMENT_METHODS[transaction.paymentMethod as keyof typeof PAYMENT_METHODS]}
                </span>
              </div>
            </div>

            {/* Farm/Cow info */}
            {(transaction.farmName || transaction.cowCode) && (
              <div className="mt-2 text-xs text-text-tertiary">
                {transaction.farmName && <span>ฟาร์ม: {transaction.farmName}</span>}
                {transaction.farmName && transaction.cowCode && <span className="mx-2">•</span>}
                {transaction.cowCode && <span>โค: {transaction.cowCode}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Right - Amount */}
        <div className="text-right">
          <p
            className={`text-xl font-bold ${
              isIncome ? 'text-success' : 'text-error'
            }`}
          >
            {isIncome ? '+' : '-'}{transaction.amount.toLocaleString('th-TH')} ฿
          </p>
          {transaction.receiptNumber && (
            <p className="text-xs text-text-tertiary mt-1">
              #{transaction.receiptNumber}
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}
